import { apiCall, APIValidationError } from '../api';
import { Await, Result } from '../api.d';
import {
  getReviewsResponse,
  UpdateAssignment,
  UpdateAssignmentResponse,
  DeleteAssignment,
  AssignableType,
} from './types';
import { put, takeLatest } from '@redux-saga/core/effects';
import assignmentSlice from './slice';
import errorSlice from '../error/slice';
import formSlice from '../form/slice';
import { FileUploadUpdaterDTO, UploadFeedbackResponse } from '../submission/types';
import { PayloadAction } from '@reduxjs/toolkit';
import tableSlice from '../table/slice';
import infoSlice from '../info/slice';
import { deleteCustomColumnsData } from '../table/commons';
import dashboardSlice from '../dashboard/slice';
import i18next from '../../i18n';
import { IgnoreConflict } from '../bid/types';
import { baseURL } from '../root-saga';
import { can } from '../../helpers/permissions';

export const postAssignmentsBulk = async (
  data: FileUploadUpdaterDTO & { from_role_id: number },
): Promise<Result<UploadFeedbackResponse, APIValidationError>> => {
  const { file, dry_run, from_role_id } = data;

  const formData = new FormData();
  formData.append('file', file);

  const init = {
    method: 'POST',
    auth: true,
    role: true,
    defaultContentType: true,
    body: formData,
  };

  return apiCall<UploadFeedbackResponse>(
    `${baseURL}/api/assignments/bulk?dry_run=${dry_run ? 1 : 0}&from_role_id=${from_role_id}`,
    init,
  );
};

const getReviews = async (): Promise<Result<getReviewsResponse, APIValidationError>> => {
  const init = {
    method: 'GET',
    auth: true,
    role: true,
  };
  return apiCall<getReviewsResponse>(`${baseURL}/api/assignments/current`, init);
};

function* getReviewsSaga() {
  const result = (yield getReviews()) as Await<ReturnType<typeof getReviews>>;

  switch (result.type) {
    case 'ok':
      const { answerById } = result.value.data;
      yield put(formSlice.actions['GET:ANSWERS:OK']({ answerById }));
      yield put(assignmentSlice.actions['GET_REVIEWS:OK'](result.value));
      if (can('ACCESS_SUMMARIZED_VIEWS')) {
        yield put(assignmentSlice.actions.CHANGE_VIEWMODE('table'));
      }
      break;
    case 'validation-error':
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({ message: result.value.validation[0].message, severity: 'error' }),
      );
      break;
    case 'error':
      break;
    default:
      break;
  }
  return;
}

const getAssignmentDetails = async (submissionId: number): Promise<Result<getReviewsResponse, APIValidationError>> => {
  const init = {
    method: 'GET',
    auth: true,
    role: true,
  };
  return apiCall<getReviewsResponse>(`${baseURL}/api/assignments/${submissionId}`, init);
};

function* getAssignmentDetailsSaga(action: PayloadAction<number>) {
  const result = (yield getAssignmentDetails(action.payload)) as Await<ReturnType<typeof getAssignmentDetails>>;

  switch (result.type) {
    case 'ok':
      const { answerById } = result.value.data;
      yield put(formSlice.actions['GET:ANSWERS:OK']({ answerById }));
      yield put(assignmentSlice.actions['GET_REVIEWS:OK'](result.value));
      break;
    case 'validation-error':
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({ message: result.value.validation[0].message, severity: 'error' }),
      );
      break;
    case 'error':
      break;
    default:
      break;
  }
  return;
}

const assignmentUpdate = async (
  data: UpdateAssignment,
): Promise<Result<UpdateAssignmentResponse, APIValidationError>> => {
  const { person_role_id, assignable_id, assignable_type, assigned, skipConflicts } = data;
  const init = {
    method: 'POST',
    auth: true,
    role: true,
    body: JSON.stringify({
      person_role_id,
      assignable_id,
      assignable_type,
      assigned,
      skip_conflicts: skipConflicts,
    }),
  };

  return apiCall<UpdateAssignmentResponse>(`${baseURL}/api/assignments/assign`, init);
};

function* assignmentsUpdateSaga(action: PayloadAction<UpdateAssignment>) {
  const result = (yield assignmentUpdate(action.payload)) as Await<ReturnType<typeof assignmentUpdate>>;

  switch (result.type) {
    case 'ok':
      const { tableFriendlyName, modelId, queryParams } = action.payload;
      const { id: assignment_id, completed } = result.value.data;
      const assigned = assignment_id > 0;

      /* If assignment has been successful */
      if (assigned == action.payload.assigned) {
        yield put(
          infoSlice.actions['ASSIGNMENTS:UPDATE:OK']({
            assignment_id,
            completed,
            modelId,
            tableFriendlyName,
          }),
        );
        yield put(tableSlice.actions['CUSTOMCOLUMNS:DATA:GET']({ tableFriendlyName, queryParams }));
        yield put(
          tableSlice.actions['NOTIFIABLES:UPDATE']({
            assignment_id: assigned ? assignment_id : action.payload.assignmentId,
            assigned,
          }),
        );
        yield deleteCustomColumnsData(tableFriendlyName); // Invalidate aggregated column data so that it will be re-fetched when accessing back.
        yield put(dashboardSlice.actions.reset());
      } else {
        yield put(
          errorSlice.actions['OPEN:SNACKBAR']({
            message: i18next.t(`Errors found while assigning.`),
            severity: 'error',
          }),
        );
      }
      yield put(assignmentSlice.actions['ASSIGNMENTS:UPDATE:OK']()); // This updates the "loading" within column cell
      break;
    case 'validation-error':
      yield put(errorSlice.actions['SET:VALIDATION_ERRORS'](Object.values(result.value.validation)));
      break;
    default:
      break;
  }
  return;
}

const assignmentDelete = async (
  data: DeleteAssignment & { assignable_type: AssignableType; field: 'person_role_id' | 'assignable_id' },
): Promise<Result<DeleteAssignment, APIValidationError>> => {
  const init = {
    method: 'DELETE',
    auth: true,
    role: true,
    body: JSON.stringify(data),
  };

  return apiCall<DeleteAssignment>(`${baseURL}/api/assignments`, init);
};

function* assignmentsDeleteSaga(action: PayloadAction<DeleteAssignment>) {
  const { tableFriendlyName } = action.payload;

  let assignable_type: AssignableType, field: 'person_role_id' | 'assignable_id';
  switch (tableFriendlyName) {
    case 'reviews_to_papers':
      assignable_type = 'App\\Models\\Submission';
      field = 'person_role_id';
      break;
    case 'papers_to_reviews':
      assignable_type = 'App\\Models\\Submission';
      field = 'assignable_id';
      break;
    default:
      throw new Error('Invalid value for tableFriendlyName.');
  }
  const result = (yield assignmentDelete({ ...action.payload, assignable_type, field })) as Await<
    ReturnType<typeof assignmentDelete>
  >;

  switch (result.type) {
    case 'ok':
      yield deleteCustomColumnsData(tableFriendlyName);
      yield put(infoSlice.actions.GET({ friendlyName: tableFriendlyName }));
      yield put(dashboardSlice.actions.reset());
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({
          message: i18next.t(`Assignments deleted successfully.`),
          severity: 'success',
        }),
      );
      break;
    case 'validation-error':
      break;
    default:
      break;
  }
  return;
}

const ignoreConflict = async (assignment_id: number): Promise<Result<{ data: any }, APIValidationError>> => {
  const init = {
    method: 'POST',
    auth: true,
    role: true,
    body: JSON.stringify({ assignment_id }),
  };

  return apiCall<{ data: any }>(`${baseURL}/api/conflicts/ignore`, init);
};

function* ignoreConflictSaga(action: PayloadAction<IgnoreConflict>) {
  const result = (yield ignoreConflict(action.payload.assignment_id)) as Await<ReturnType<typeof ignoreConflict>>;

  switch (result.type) {
    case 'ok':
      yield put(infoSlice.actions['ASSIGNMENTS:IGNORE_CONFLICT:OK'](action.payload));
      break;
    case 'validation-error':
      yield put(errorSlice.actions['SET:VALIDATION_ERRORS'](Object.values(result.value.validation)));
      break;
    default:
      break;
  }
  return;
}

export default [
  takeLatest(assignmentSlice.actions['GET_REVIEWS'], getReviewsSaga),
  takeLatest(assignmentSlice.actions['GET_ASSIGNMENT_DETAILS'], getAssignmentDetailsSaga),
  takeLatest(assignmentSlice.actions['ASSIGNMENTS:UPDATE'], assignmentsUpdateSaga),
  takeLatest(assignmentSlice.actions['ASSIGNMENTS:DELETE'], assignmentsDeleteSaga),
  takeLatest(assignmentSlice.actions['ASSIGNMENTS:IGNORE_CONFLICT'], ignoreConflictSaga),
];
